import React, { useState, useEffect } from 'react'
import { graphql, useFragment } from 'react-relay/hooks'
import {
  FlexRow,
  FlexColumn,
  Text,
  Select,
  TabBar,
  Button,
  Spinner,
  Icon,
  SuspenseBoundary,
} from 'elements'
import { formatSectionName } from '../utils/formatting'
import { ResolutionStatus } from '../types'
import useReportResolutionsApply from './hooks/useReportResolutionsApply'
import useReportResolutionUpdateStatuses from './hooks/useReportResolutionUpdateStatuses'
import { Option } from '../../../shared/types'
import { resolutionSectionTabs } from './constants'
import { useAnalytics } from 'hooks'
import ReportResolutionTable from './ReportResolutionTable'

type Props = {
  report: any
  resolutionSection: ResolutionStatus
  onResolutionSectionChange: Function
  anomalyView: string
  onAnomalyViewChange: Function
}

type AnomalyCount = {
  type: string
  count: number
}

export default function ReportResolution(props: Props) {
  const {
    report,
    resolutionSection,
    onResolutionSectionChange,
    anomalyView,
    onAnomalyViewChange,
  } = props
  const [selectedRows, setSelectedRows] = useState([])
  const [applyProcessing, setApplyProcessing] = useState(false)
  const [applyResolutions, applyInFlight] = useReportResolutionsApply()
  const [updateResolutionStatuses, updateInFlight] = useReportResolutionUpdateStatuses()

  const analytics = useAnalytics()

  const data = useFragment(
    graphql`
      fragment ReportResolution_report on Report
        @refetchable(queryName: "ReportResolutionRefetchQuery") {
        id
        reviewStatus
        pendingResolutions: reportResolutions(condition: { updateStatus: "pending" }) {
          totalCount
        }
        anomalyCounts {
          type
          count
        }
      }
    `,
    report,
  )

  const hasPendingResolutions = data.pendingResolutions.totalCount > 0

  const { reviewStatus } = data

  useEffect(() => {
    if (applyInFlight || reviewStatus === 'processing') {
      setApplyProcessing(true)
    } else {
      setApplyProcessing(false)
    }
  }, [applyInFlight, reviewStatus])

  const anomalyOptions = data.anomalyCounts.map((anomalyCount: AnomalyCount) => ({
    label: formatSectionName(anomalyCount.type),
    value: anomalyCount,
  }))

  const hasSelected = !!selectedRows.length

  return (
    <FlexColumn>
      <FlexRow alignItems="center" justifyContent="space-between" mb="xxxlarge">
        <FlexColumn width="30%" mr="xlarge" justifyContent="center">
          {/* @ts-ignore */}
          <Select
            options={anomalyOptions}
            value={anomalyOptions.find((option: Option) => option.value.type === anomalyView)}
            placeholder="Select Anomaly"
            onChange={(option: Option) => onAnomalyViewChange(option.value.type)}
            formatOptionLabel={(option: Option, meta: any) => {
              const { label, value } = option
              const { context } = meta

              return (
                <FlexRow justifyContent="space-between">
                  <Text>{label}</Text>
                  {context === 'menu' && <Text>{value.count}</Text>}
                </FlexRow>
              )
            }}
          />
        </FlexColumn>
        <FlexRow justifyContent="flex-end" alignItems="center">
          {hasPendingResolutions && (
            <FlexRow justifyContent="center" alignItems="center" mr="large">
              <Icon name="exclamation-circle" size="medium" color="neutral.3" />
              <Text fontStyle="italic" color="neutral.3">
                You have unsynced changes
              </Text>
            </FlexRow>
          )}
          <Button
            kind="primary"
            width="xgigantic"
            disabled={applyProcessing}
            onClick={async () => {
              // @ts-ignore
              await applyResolutions(
                { reportId: data.id },
                {
                  anomalies: anomalyView === 'all' ? null : [anomalyView],
                  resolutionSection,
                },
              )

              analytics.track('Resolutions Synced', { reportId: data.id })
            }}
          >
            {applyProcessing ? (
              <FlexRow justifyContent="space-between" alignItems="center">
                <FlexRow mr="small">
                  <Spinner size="large" />
                </FlexRow>
                Syncing...
              </FlexRow>
            ) : (
              'Sync'
            )}
          </Button>
        </FlexRow>
      </FlexRow>
      <FlexRow mb="medium" justifyContent="center" position="relative">
        {hasSelected && resolutionSection === 'forReview' && (
          <FlexRow position="absolute" left="0">
            <Button
              disabled={updateInFlight}
              onClick={async () => {
                // @ts-ignore
                await updateResolutionStatuses(
                  {
                    reportId: data.id,
                    resolutionIds: selectedRows.map((row: any) => row.resolution.id),
                    status: 'reviewed',
                  },
                  {
                    anomalies: anomalyView === 'all' ? null : [anomalyView],
                    resolutionSection,
                  },
                )

                analytics.track('Resolutions Reviewed', { reportId: data.id })

                // selected rows are not automatically updating when the rows change sections
                setSelectedRows([])
              }}
              mb="large"
            >
              Mark as Reviewed
            </Button>
          </FlexRow>
        )}
        <TabBar
          tabs={resolutionSectionTabs}
          value={resolutionSection}
          onChange={(tab: Option) => onResolutionSectionChange(tab.value)}
          styleProps={{ color: 'neutral.3', borderColor: 'accent.lightblue' }}
        />
      </FlexRow>
      <SuspenseBoundary>
        <ReportResolutionTable
          report={report}
          resolutionSection={resolutionSection}
          anomalyView={anomalyView}
          onRowSelect={setSelectedRows}
        />
      </SuspenseBoundary>
    </FlexColumn>
  )
}
